import { useState, useEffect, useCallback } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "dx_admin_token";

// sessionStorage clears on tab close — narrower XSS exposure than localStorage.
const readToken = () => {
  try { return sessionStorage.getItem(TOKEN_KEY) || ""; }
  catch (e) { console.warn("sessionStorage read failed:", e); return ""; }
};

const writeToken = (tok) => {
  try { sessionStorage.setItem(TOKEN_KEY, tok); }
  catch (e) { console.warn("sessionStorage write failed:", e); }
};

const clearToken = () => {
  try { sessionStorage.removeItem(TOKEN_KEY); }
  catch (e) { console.warn("sessionStorage clear failed:", e); }
};

const loginErrorFor = (status, detail) => {
  if (typeof detail === "string" && detail) return detail;
  if (status === 401) return "Incorrect PIN";
  if (status === 429) return "Too many attempts. Try again in 15 minutes.";
  return "Login failed";
};

export function useAdminAuth() {
  const [token, setToken] = useState(readToken);
  const [pin, setPin] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const logout = useCallback(() => {
    clearToken();
    setToken("");
  }, []);

  const login = useCallback(async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin }),
      });
      // PostHog/analytics may intercept fetch; clone before reading.
      let data = {};
      try { data = await res.clone().json(); } catch { data = {}; }
      if (!res.ok) {
        setLoginError(loginErrorFor(res.status, data.detail));
        return;
      }
      writeToken(data.token);
      setToken(data.token);
      setPin("");
    } catch {
      setLoginError("Connection error. Please try again.");
    } finally {
      setLoggingIn(false);
    }
  }, [pin]);

  return { token, logout, pin, setPin, loginError, loggingIn, login };
}

export function useLeads(token, onUnauthorized) {
  const [leads, setLeads] = useState(null);

  const loadLeads = useCallback(async () => {
    if (!token) return;
    const res = await fetch(`${API}/api/admin/leads`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 401) {
      onUnauthorized();
      return;
    }
    setLeads(await res.json());
  }, [token, onUnauthorized]);

  useEffect(() => { loadLeads(); }, [loadLeads]);

  const updateStatus = useCallback(async (leadId, status) => {
    setLeads((prev) => (prev || []).map((l) => (l.id === leadId ? { ...l, status } : l)));
    await fetch(`${API}/api/admin/leads/${leadId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status }),
    });
  }, [token]);

  return { leads, loadLeads, updateStatus };
}
